import { CardChoice } from 'components/domain/ProfilingForm/CardChoice';
import { Field, FieldProps, useFormikContext } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BaseFormField } from 'types/form/baseFormField';
import { DropdownItem } from '../Dropdown';

export interface FormikSelectCardProps extends BaseFormField {
  items: DropdownItem[];
  isMultiple?: boolean;
  disabled?: boolean;
  maxSelections?: number;
  requireSelection?: boolean;
  disableKeys?: (string | number)[];
  className?: string;
}

export const FormikSelectCard: React.FC<FormikSelectCardProps> = ({
  name,
  items,
  label,
  required,
  disabled,
  isMultiple = false,
  maxSelections,
  requireSelection = false,
  disableKeys = [],
  className
}) => {
  const [selectedItems, setSelectedItems] = useState<DropdownItem[]>([]);
  const { setFieldValue, setFieldTouched, setFieldError, values } = useFormikContext();

  // Use refs to track previous values and prevent unnecessary updates
  const previousValueRef = useRef<any>();
  const isUpdatingFromFormikRef = useRef(false);

  // Helper function to check if an item is disabled
  const isItemDisabled = useCallback((item: DropdownItem): boolean => {
    return disabled || item.disabled || disableKeys.includes(item.key);
  }, [disabled, disableKeys]);

  // Helper function to get selected items from form value
  const getSelectedItemsFromValue = useCallback((value: any): DropdownItem[] => {
    if (isMultiple) {
      if (Array.isArray(value)) {
        return items.filter(item => value.includes(item.key));
      }
      return [];
    } else {
      if (value) {
        const foundItem = items.find(item => item.key === value);
        return foundItem ? [foundItem] : [];
      }
      return [];
    }
  }, [items, isMultiple]);

  // Sync with form values (only when form value actually changes)
  useEffect(() => {
    const currentValue = (values as any)[name];

    // Only update if the value actually changed
    if (currentValue !== previousValueRef.current) {
      previousValueRef.current = currentValue;
      isUpdatingFromFormikRef.current = true;

      const newSelectedItems = getSelectedItemsFromValue(currentValue);
      setSelectedItems(newSelectedItems);

      // Reset the flag after state update
      setTimeout(() => {
        isUpdatingFromFormikRef.current = false;
      }, 0);
    }
  }, [(values as any)[name], getSelectedItemsFromValue]);

  // Sync selection changes to Formik (only when not updating from Formik)
  useEffect(() => {
    // Don't update Formik if we're currently updating from Formik
    if (isUpdatingFromFormikRef.current) {
      return;
    }

    const newValue = isMultiple
      ? selectedItems.map(item => item.key)
      : selectedItems[0]?.key || null;

    const currentValue = (values as any)[name];

    // Only update if the value is actually different
    if (JSON.stringify(newValue) !== JSON.stringify(currentValue)) {
      setFieldTouched(name, true, false);
      setFieldValue(name, newValue, false);
      setFieldError(name, undefined);
    }
  }, [selectedItems, name, setFieldValue, setFieldTouched, setFieldError, isMultiple, values]);

  const handleSelect = useCallback((item: DropdownItem) => {
    if (isItemDisabled(item)) return;

    setSelectedItems(prevSelected => {
      if (isMultiple) {
        const isCurrentlySelected = prevSelected.some(selected => selected.key === item.key);

        if (isCurrentlySelected) {
          // Empêcher la désélection si requireSelection est true et qu'il ne reste qu'un élément
          if (requireSelection && prevSelected.length === 1) {
            return prevSelected;
          }
          // Deselect the item
          return prevSelected.filter(selected => selected.key !== item.key);
        } else {
          // Select the item (with maxSelections check)
          if (maxSelections && prevSelected.length >= maxSelections) {
            return prevSelected; // Don't add if limit is reached
          }
          return [...prevSelected, item];
        }
      } else {
        // Single mode
        const isCurrentlySelected = prevSelected.length > 0 && prevSelected[0].key === item.key;

        if (isCurrentlySelected) {
          // Empêcher la désélection si requireSelection est true
          if (requireSelection) {
            return prevSelected;
          }
          // Deselect
          return [];
        } else {
          // Select
          return [item];
        }
      }
    });
  }, [isItemDisabled, isMultiple, maxSelections, requireSelection]);

  return (
    <Field name={name}>
      {({ meta }: FieldProps) => (
        <div className={className}>
          {label && (
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}

          <div className="flex flex-wrap gap-2 justify-center items-stretch">
            {items.map((item) => (

              <div
                key={item.key}
                className="flex-grow min-w-0 basis-1/2 sm:basis-1/3 md:basis-1/5 lg:basis-1/5 xl:basis-1/6"
              >
                <CardChoice
                  key={item.key}
                  className='w-full h-full'
                  title={item.label}
                  iconKey={item.icon}
                  isSelected={selectedItems.some(selected => selected.key === item.key)}
                  disabled={isItemDisabled(item)}
                  onClick={() => handleSelect(item)}
                  {...(item as any)} // Spread other props if necessary
                />
              </div>
            ))}
          </div>

          {isMultiple && maxSelections && (
            <p className="text-xs text-gray-500 mt-2">
              {selectedItems.length}/{maxSelections} sélection{maxSelections > 1 ? 's' : ''}
            </p>
          )}

          {meta.touched && meta.error && (
            <p className="text-red-500 text-xs mt-2">{meta.error}</p>
          )}
        </div>
      )}
    </Field>
  );
};
